import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Center, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import api from '../services/api';

/**
 * Resolver for the stable QR code alias /m/:id.
 *
 * Printed QR codes encode /m/:id where :id is a standalone QrCode record id
 * (or, for legacy codes, a raw menu id). This page asks the backend which menu
 * the code currently points at and redirects there, so printed codes keep
 * working when the underlying menu is replaced, repointed, or the menu view
 * itself is overhauled.
 */
const MenuAlias: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [menuId, setMenuId] = useState<string | null>(null);
  const [landingView, setLandingView] = useState<'menu' | 'model_select'>('menu');
  const [unassigned, setUnassigned] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      try {
        const response = await api.get(`/qrcodes/resolve/${id}`);
        if (cancelled) return;
        if (response.data?.menuId) {
          setMenuId(response.data.menuId);
          setLandingView(response.data.landingView === 'model_select' ? 'model_select' : 'menu');
        } else {
          // QR code exists but isn't pointing at a menu right now
          setUnassigned(true);
        }
      } catch {
        if (cancelled) return;
        // Resolution failed (unknown id or backend hiccup) — fall back to treating
        // the id as a menu id so legacy printed codes still work.
        setMenuId(id ?? null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  if (menuId) {
    // Preserve query params (e.g. ?lang=source) across the redirect.
    // A QR code can opt into the experimental AI-assistant picker landing page.
    const pathname =
      landingView === 'model_select' ? `/menus/${menuId}/start` : `/menus/${menuId}`;
    return <Navigate to={{ pathname, search: location.search }} replace />;
  }

  return (
    <Center h="100vh" p="md">
      <Paper withBorder p="xl" maw={420}>
        <Stack align="center" gap="sm">
          <Title order={3} ta="center">
            {unassigned ? 'Menu coming soon' : 'Menu not found'}
          </Title>
          <Text c="dimmed" ta="center">
            {unassigned
              ? 'This QR code is valid but is not linked to a menu right now. Please check back shortly.'
              : 'This QR code could not be resolved. Please ask the restaurant staff for an updated code.'}
          </Text>
        </Stack>
      </Paper>
    </Center>
  );
};

export default MenuAlias;
